import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { baseApi } from '@/services/baseApi';
import { generatedApi } from '@/services/generated/api';
import { sentryErrorMiddleware } from './sentryMiddleware';
import { authReducer, initialAuthState, type AuthState } from '@/features/auth/authSlice';
import {
  readPersistedSelection,
  writePersistedSelection,
} from '@/features/auth/auth-storage';

/**
 * When the active tenant changes, the API's whole cache stops being valid.
 *
 * The tenant travels in the `X-Tenant-Id` header (see `baseApi.ts`), and **a
 * header is not part of RTK Query's cache key**: `useCompanyControllerListQuery()`
 * has the same arguments — none — before and after switching organisation, so
 * without this the app would go on showing the previous tenant's companies under
 * the new one's name in the bar. It is the kind of lie this product cannot
 * afford: the active company is what decides where a folio is consumed.
 *
 * It fires by comparing the state BEFORE and AFTER, not by listening to the
 * action. The reason is that `availableTenantsResolved` is dispatched on every
 * refetch of `GET /api/account` and almost never changes anything: resetting on
 * the action would loop `reset → refetch → action → reset` forever. Comparing
 * the value, the cycle breaks itself as soon as the id settles.
 *
 * The first login goes from `null` to an id, so it resets there too — and that
 * fixes the initial-load race: the queries that went out before there was an
 * active tenant (with no header) are requested again with it.
 */
const tenantChangeListener = createListenerMiddleware();

tenantChangeListener.startListening({
  predicate: (_action, currentState, previousState) => {
    const before = (previousState as { auth: { activeTenantId: string | null } }).auth
      .activeTenantId;
    const after = (currentState as { auth: { activeTenantId: string | null } }).auth
      .activeTenantId;
    return before !== after;
  },
  effect: (_action, api) => {
    api.dispatch(baseApi.util.resetApiState());
  },
});

/**
 * Records the identity strip's selection on every change: in `localStorage` for
 * this browser, and on the account for every OTHER browser.
 *
 * It is a listener and not a call at the click site for the same reason the
 * cache reset is: the two ids move from FOUR places — the strip's two selectors,
 * the reconciliation that picks a default when what is held is no good, the
 * tenant switch that clears the company, and logging out — and a writer at each
 * of them is a writer the fifth one will forget. Comparing before and after
 * catches every path by construction, and writes nothing when nothing moved.
 *
 * ## Two destinations, because they answer different questions
 *
 * `localStorage` is SYNCHRONOUS and available before the first render, which is
 * what lets `makeStore` boot already on the right empresa instead of painting
 * the wrong one and correcting itself. It covers exactly one browser profile.
 *
 * `PUT /api/account/selection` is what follows the person: a new device, another
 * browser, cleared site data. It cannot replace the local copy — it is not
 * readable until `GET /api/account` answers — so it is not a substitute but the
 * other half. It is fire-and-forget on purpose: the strip has already moved, the
 * call carries no information the UI is waiting for, and a failed write costs a
 * default on the next unknown device, which is what happened before it existed.
 * Idempotent, so a redundant write (the reconciliation adopting the value the
 * server itself published, on boot) is a no-op.
 *
 * With no active organisation there is nothing to key the preference on and
 * nothing is sent — which is also what leaves the remembered pair standing
 * through a logout, where both ids go to `null`.
 *
 * ## Two details that look wrong and are not
 *
 * `track: false` keeps the call out of RTK Query's mutation state. Nothing
 * renders its result, and a tracked entry per click on the strip would
 * accumulate for as long as the tab lives.
 *
 * The endpoint DOES invalidate the `Account` tag, and that refetch is
 * load-bearing rather than waste: the empresa restored on switching organisation
 * is read from the cached account, so an account left stale would send someone
 * who switches away and back to the empresa they had BEFORE their last choice.
 * The refetch settles in one round — the reconciliations it re-runs find both
 * ids already valid and change nothing, so no state moves and this listener does
 * not fire again.
 */
const selectionPersistenceListener = createListenerMiddleware();

function selectionOf(state: unknown): { tenantId: string | null; companyId: string | null } {
  const { auth } = state as { auth: AuthState };
  return { tenantId: auth.activeTenantId, companyId: auth.activeCompanyId };
}

selectionPersistenceListener.startListening({
  predicate: (_action, currentState, previousState) => {
    const before = selectionOf(previousState);
    const after = selectionOf(currentState);
    return before.tenantId !== after.tenantId || before.companyId !== after.companyId;
  },
  effect: (_action, api) => {
    const selection = selectionOf(api.getState());
    writePersistedSelection(selection);
    if (selection.tenantId === null) return;
    void api.dispatch(
      generatedApi.endpoints.accountControllerUpdateSelection.initiate(
        {
          updateSelectionDto: {
            tenantId: selection.tenantId,
            companyId: selection.companyId,
          },
        },
        { track: false },
      ),
    );
  },
});

/**
 * The store's factory. The app uses the singleton `store` instance, hydrated
 * from storage; the tests create a new one per case to isolate RTK Query's
 * cache, and pass the auth state they need instead of touching `localStorage`.
 */
export function makeStore(preloadedAuth?: Partial<AuthState>) {
  return configureStore({
    reducer: {
      [baseApi.reducerPath]: baseApi.reducer,
      auth: authReducer,
    },
    preloadedState: preloadedAuth
      ? { auth: { ...initialAuthState, ...preloadedAuth } }
      : undefined,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(tenantChangeListener.middleware, selectionPersistenceListener.middleware)
        // After RTK Query's middleware: it reports what that one already resolved as a
        // rejection, without interfering with the cache's lifecycle.
        .concat(baseApi.middleware, sentryErrorMiddleware),
  });
}

/**
 * The application's store, starting from the selection this browser last had.
 *
 * Restoring is safe before the account has resolved because nothing in the data
 * plane goes out first: `AppLayout` gates every query — and the `<Outlet/>`
 * itself — on `GET /api/account` having arrived AND the active tenant being one
 * of the tenants it names. A stale id therefore never reaches a request; it is
 * reconciled away first.
 */
const persisted = readPersistedSelection();
export const store = makeStore({
  activeTenantId: persisted.tenantId,
  activeCompanyId: persisted.companyId,
});

// Enables RTK Query's refetch on focus/reconnect.
setupListeners(store.dispatch);

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
